import { useCallback, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  createColumnHelper,
  DataTable,
  DataTableCellContent,
  DataTableCellTrigger,
  DataTableEmpty,
  DataTableGroupByDropdown,
  EntitySelectorContent,
  EntitySelectorFooter,
  EntitySelectorFooterAction,
  EntitySelectorInput,
  EntitySelectorPool,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
  resolveVisibleGroups,
  Text,
  useDataTableState,
  type BulkAction,
  type BulkActionPopover,
  type DataTableColumnDef,
  type DataTableGroupFieldConfig,
  type DataTableGroupType,
  type EntityItemConfig,
  type Omittable,
  type SortingState,
  type SpacingToken,
} from "@salestrak/ui";

import { EntityTag } from "#app/components/entity";
import { UserSelector } from "#app/components/entity-selectors";
import { USER_POOL_ID } from "#app/components/entity-selectors/hooks/use-user-pool";
import { ViewSearch } from "#app/components/filters/view-search";
import {
  AnalyticsContext,
  MemberEvent,
  TeamEvent,
  trackEvent,
  useTrackSearch,
} from "#app/lib/analytics";
import {
  MEMBER_FILTER_KEYS,
  MemberGroupChip,
  MemberQuickFilters,
  MemberQuickFiltersDivider,
  MemberSortChip,
  PRESENCE_OPTIONS,
  buildMemberQuickFilterDefs,
} from "#app/components/members/member-quick-filters";
import { MEMBER_STATUS_BADGE } from "#app/components/members/member-status-badge";
import {
  MemberEmailCell,
  MemberManagerCell,
  MemberTeamsDisplayCell,
} from "#app/components/members/members-table";
import {
  LocalGroupProvider,
  useGroupStates,
  type LocalGroupProviderProps,
} from "#app/lib/hooks/group-query-provider";
import { usePresenceMap } from "#app/lib/data/presence-provider";
import { useMemberLabelResolver } from "#app/lib/data/member-directory-provider";
import {
  useTeamMembers,
  type TeamMember,
} from "#app/lib/hooks/use-team-members";
import { useSetReportsTo } from "#app/lib/hooks/use-set-reports-to";
import { useTeamMutations } from "#app/lib/hooks/use-team-mutations";
import { TeamAddMemberAction } from "#app/components/teams/team-add-member-action";
import {
  useAppData,
  type TeamMembershipRole,
  connectionRows,
} from "#app/lib/data";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useMembersSiblingData } from "#app/lib/hooks/use-members-sibling-data";
import { useTableFilters } from "#app/lib/hooks/use-table-filters";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { MEMBER_LIST } from "#app/lib/filters/list-configs";
import { buildSearchWhere, combineWhereConditions } from "#app/lib/query-utils";
import { settleAll, settleInOrder } from "#app/lib/utils/settle-all";

// The bulk bar's count-chip noun: "1 Member selected".
const MEMBER_SELECTION_NOUN = { one: "Member", other: "Members" };

/**
 * The single team roster: the Name/Reports to/Teams/Email/Role/Status table
 * (Joined ships hidden behind the column picker) plus, for viewers
 * who can manage membership, the role menu, the row actions (promote/demote,
 * remove/leave with its confirm dialog) and the add-members selector. The
 * selector's footer holds the invite-by-email escape hatch (grant-driven via
 * `useCanInvite`, seeding this team in the invite dialog — note the server
 * does not yet apply `teamIds` on invites, so accepted invitees still need
 * adding to the roster) — the one entry point for people not yet in the
 * workspace. Both
 * hosts — the team page's Members tab and the settings teammates page —
 * compose this and own only their chrome, so the two rosters cannot drift.
 *
 * Vocabulary: the elevated team role (`role: "lead"` on the wire) reads
 * "Manager" — the word appears ONLY in team context. The reporting line is
 * always the relationship "Reports to", never a "Manager" person-label, so
 * the two facts cannot be confused when they land on the same person.
 *
 * `canManage` is the server-computed `Team.viewerCanManage` (workspace-wide
 * `manage.teams` OR a manager of this specific team) — hosts pass it down,
 * never re-derive it. Read-only viewers get the same table with plain role
 * badges and no management affordances.
 */

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

const h = createColumnHelper<TeamMember>();

// The selection lane leads, then the identity column — the one pinned span
// every bulk-select table shares (members, teams, projects): pinning takes
// both out of the reorderable span, so neither the header context menu nor
// a drag can move them or land another column ahead of them.
const ROSTER_PINNED_COLUMN_IDS = ["select", "name"];

// Joined ships hidden; the column picker can reveal it (the members-table
// app-variant treatment).
const ROSTER_DEFAULT_HIDDEN_COLUMN_IDS = ["creationDate"];

// Group-by buckets, both fixed enums in their canonical order. Team role
// carries the Role column's own vocabulary ("lead" on the wire reads
// "Manager"); presence buckets are keyed by the GraphQL enum names the
// Status chip's presence facet writes, so an active presence pick narrows
// the buckets through `resolveVisibleGroups`.
const TEAM_ROLE_GROUPS = [
  { key: "lead", label: "Manager" },
  { key: "member", label: "Member" },
];
const PRESENCE_GROUPS = PRESENCE_OPTIONS.map((o) => ({
  key: o.value,
  label: o.label,
}));

// Module-scoped, NOT an inline literal: `useDataTableState` derives
// `activeGroupConfig` from this object's identity, `visibleGroups` derives
// from that, and the bucket arrays derive from THAT — a per-render literal
// here re-partitions every render, and each partition's fresh arrays make
// `useLocalGroupPages` publish fresh state, which re-renders the roster: an
// infinite update loop.
const ROSTER_GROUP_FIELDS = {
  role: {
    // "Team Role", not "Role": the chips row already has a Role facet, and
    // that one is the WORKSPACE role.
    label: "Team Role",
    icon: "shield",
    groups: TEAM_ROLE_GROUPS,
    preserveOrder: true,
  },
  presence: {
    label: "Presence",
    icon: "circle",
    groups: PRESENCE_GROUPS,
    // The Status chip's presence facet: a pick there narrows the buckets.
    filterKey: MEMBER_FILTER_KEYS.presence,
    preserveOrder: true,
  },
} satisfies Record<string, DataTableGroupFieldConfig>;

// Plain text, not a Badge — role cells read as ordinary values (the
// members-table Role treatment).
function RoleLabel({ role }: { role: TeamMembershipRole }) {
  return (
    <Text size="sm" variant="muted">
      {role === "lead" ? "Manager" : "Member"}
    </Text>
  );
}

// ---------------------------------------------------------------------------
// RoleMenuCell — matches DataTablePopoverCell pattern (fills cell, active border)
// ---------------------------------------------------------------------------

type RoleMenuCellProps = {
  member: TeamMember;
  onChangeRole: (member: TeamMember, newRole: TeamMembershipRole) => void;
};

function RoleMenuCell({ member, onChangeRole }: RoleMenuCellProps) {
  const [open, setOpen] = useState(false);
  return (
    // Base UI's Menu root renders no DOM node, so there is nothing to stretch
    // to the cell — the trigger fills the cell itself.
    <Menu open={open} onOpenChange={setOpen}>
      <MenuTrigger render={<DataTableCellTrigger active={open} />}>
        <DataTableCellContent>
          <RoleLabel role={member.role} />
        </DataTableCellContent>
      </MenuTrigger>
      <MenuPopup align="start">
        <MenuItem
          disabled={member.role === "lead"}
          onClick={() => onChangeRole(member, "lead")}
        >
          Manager
          {member.role === "lead" ? <Icon name="check" size="md" color="primary" /> : null}
        </MenuItem>
        <MenuItem
          disabled={member.role === "member"}
          onClick={() => onChangeRole(member, "member")}
        >
          Member
          {member.role === "member" ? <Icon name="check" size="md" color="primary" /> : null}
        </MenuItem>
      </MenuPopup>
    </Menu>
  );
}

type UseTeamMemberColumnsParams = Omittable<{
  canManage: boolean;
  onChangeRole: (member: TeamMember, newRole: TeamMembershipRole) => void;
  /** Makes the Reports-to cell editable — passed only when the viewer holds
   *  manage.users (the reporting line's one owner), like every other mount
   *  of the column. */
  onManagerChange?: (userId: string, managerId: string | null) => void;
}>;

function useTeamMemberColumns({
  canManage,
  onChangeRole,
  onManagerChange,
}: UseTeamMemberColumnsParams): DataTableColumnDef<TeamMember>[] {
  return useMemo(
    () => [
      h.accessor({
        accessorKey: "name",
        header: "Name",
        enableSorting: true,
        meta: { minWidth: 200, truncate: true, width: 320 },
        cell: ({ row }) => {
          const member = row.original;
          return (
            <DataTableCellContent>
              <EntityTag
                entity={{ kind: "user", id: member.userId, user: member }}
                presence
              />
            </DataTableCellContent>
          );
        },
      }),
      // Reporting line — editable for manage.users holders (the same commit
      // as the members table and the profile panel); managerId resolves
      // through the workspace user map, so the column costs no extra
      // selection. Labeled as the relationship ("Reports to"), never
      // "Manager" — that word is the team role's.
      h.display({
        id: "manager",
        header: "Reports to",
        meta: { minWidth: 120, width: 180 },
        cell: ({ row }) => (
          <MemberManagerCell
            userId={row.original.userId}
            managerId={row.original.managerId}
            onCommit={onManagerChange}
          />
        ),
      }),
      h.display({
        id: "teams",
        header: "Teams",
        meta: { minWidth: 140 },
        cell: ({ row }) => <MemberTeamsDisplayCell teams={row.original.teams} />,
      }),
      h.accessor({
        accessorKey: "email",
        header: "Email",
        enableSorting: true,
        meta: { minWidth: 300 },
        cell: ({ getValue }) => (
          <MemberEmailCell email={(getValue() as string | null) ?? ""} />
        ),
      }),
      // Role is the TEAM role (Manager/Member) — workspace roles live on the
      // members screens.
      h.display({
        id: "role",
        header: "Role",
        meta: { minWidth: 120 },
        cell: ({ row }) =>
          canManage ? (
            <RoleMenuCell member={row.original} onChangeRole={onChangeRole} />
          ) : (
            // No outline: nothing happens on press for a read-only viewer.
            <DataTableCellContent>
              <RoleLabel role={row.original.role} />
            </DataTableCellContent>
          ),
      }),
      h.badge({
        accessorKey: "memberStatus",
        header: "Status",
        enableSorting: true,
        badgeMap: MEMBER_STATUS_BADGE,
        fallback: "active",
      }),
      h.date({ accessorKey: "creationDate", header: "Joined" }),
    ],
    [canManage, onChangeRole, onManagerChange],
  );
}

// ---------------------------------------------------------------------------
// Grouped providers — one LocalGroupProvider per bucket, rows partitioned
// client-side. The roster is a single fetched list (the LocalGroupProvider
// design), so grouping partitions that list rather than running a query per
// bucket the way the members directory does.
// ---------------------------------------------------------------------------

type RosterGroupProvidersProps = {
  visibleGroups: Array<{ key: string; label: string }>;
  rows: TeamMember[];
  contextKey: string;
  onStateChange: LocalGroupProviderProps<TeamMember>["onStateChange"];
};

function partitionRows(
  visibleGroups: Array<{ key: string; label: string }>,
  rows: TeamMember[],
  keyOf: (member: TeamMember) => string,
): Map<string, TeamMember[]> {
  const buckets = new Map<string, TeamMember[]>(
    visibleGroups.map((g) => [g.key, []]),
  );
  for (const row of rows) buckets.get(keyOf(row))?.push(row);
  return buckets;
}

function BucketProviders({
  visibleGroups,
  buckets,
  contextKey,
  onStateChange,
}: Omit<RosterGroupProvidersProps, "rows"> & {
  buckets: Map<string, TeamMember[]>;
}) {
  return (
    <>
      {visibleGroups.map((g) => (
        <LocalGroupProvider<TeamMember>
          key={g.key}
          groupKey={g.key}
          rows={buckets.get(g.key) ?? []}
          contextKey={contextKey}
          onStateChange={onStateChange}
        />
      ))}
    </>
  );
}

/** Team-role buckets: the partition key is the row's own derived `role`. */
function RoleGroupProviders({
  visibleGroups,
  rows,
  contextKey,
  onStateChange,
}: RosterGroupProvidersProps) {
  const buckets = useMemo(
    () => partitionRows(visibleGroups, rows, (m) => m.role),
    [visibleGroups, rows],
  );
  return (
    <BucketProviders
      visibleGroups={visibleGroups}
      buckets={buckets}
      contextKey={contextKey}
      onStateChange={onStateChange}
    />
  );
}

/**
 * Presence buckets. A separate component so the whole-map subscription —
 * which re-renders on ANY member's presence event — mounts only while the
 * roster is actually grouped by presence; rows then re-bucket live as
 * teammates come and go. A member with no entry (presence unknown) lands in
 * Offline — the bucket "not known to be online" honestly describes.
 */
function PresenceGroupProviders({
  visibleGroups,
  rows,
  contextKey,
  onStateChange,
}: RosterGroupProvidersProps) {
  const presenceMap = usePresenceMap();
  const buckets = useMemo(
    () =>
      partitionRows(
        visibleGroups,
        rows,
        (m) => presenceMap.get(m.userId)?.status ?? "OFFLINE",
      ),
    [visibleGroups, rows, presenceMap],
  );
  return (
    <BucketProviders
      visibleGroups={visibleGroups}
      buckets={buckets}
      contextKey={contextKey}
      onStateChange={onStateChange}
    />
  );
}

// ---------------------------------------------------------------------------
// Bulk Reports to — the profile row's single-pick member picker over many
// rows. A pick commits at once through the bar (which closes the popover and
// announces the batch); the footer's "No manager" clears the line for every
// selected member.
// ---------------------------------------------------------------------------

type BulkCommit = Parameters<BulkActionPopover<TeamMember>["content"]>[0]["commit"];

function BulkReportsToContent({
  selectedRows,
  commit,
}: {
  selectedRows: TeamMember[];
  commit: BulkCommit;
}) {
  const resolveMemberLabel = useMemberLabelResolver();
  const setReportsTo = useSetReportsTo("team_roster_bulk");

  // Pre-ticked only when every selected member already shares one manager;
  // a mixed selection starts blank rather than claiming a value it lacks.
  const sharedManagerId = useMemo(() => {
    const first = selectedRows[0]?.managerId ?? null;
    return selectedRows.every((m) => (m.managerId ?? null) === first) ? first : null;
  }, [selectedRows]);

  // One row withholds that member (the cell picker's rule). With several
  // selected, a selected member stays pickable so the rest can report to
  // them — that member's own row is skipped below, since only self is
  // refused.
  const exclude = useMemo(
    () => (selectedRows.length === 1 ? [selectedRows[0]!.userId] : undefined),
    [selectedRows],
  );

  const apply = useCallback(
    (managerId: string | null) => {
      const managerName = managerId ? resolveMemberLabel(managerId) : null;
      // The members this pick changes: not the manager themself, and not
      // those who already report to them. The toast counts the same rows.
      const targetsOf = (rows: TeamMember[]) =>
        rows.filter((m) => m.userId !== managerId && (m.managerId ?? null) !== managerId);
      const n = targetsOf(selectedRows).length;
      const noun = `${n} member${n !== 1 ? "s" : ""}`;
      commit(
        async (rows) => {
          // In order, not in parallel: a pick inside a member's own reporting
          // line makes the server lift someone, and two lifts in one line at
          // once could move the same person twice. The roster refetches when
          // the batch ends, so the cache shows the server's final line.
          await settleInOrder(targetsOf(rows), (m) =>
            setReportsTo(m.userId, managerId, { silent: true }),
          );
        },
        managerId
          ? `${noun} now report${n === 1 ? "s" : ""} to ${managerName ?? "the manager"}`
          : `Reports to cleared for ${noun}`,
      );
    },
    [commit, selectedRows, setReportsTo, resolveMemberLabel],
  );

  const anyManaged = selectedRows.some((m) => m.managerId);

  return (
    <UserSelector
      variant="inline"
      open
      title="Reports to"
      exclude={exclude}
      maxSelections={1}
      value={sharedManagerId}
      onValueChange={(ids) => apply(ids[0] ?? null)}
    >
      <EntitySelectorContent>
        <EntitySelectorInput />
        <EntitySelectorPool value={USER_POOL_ID} />
        {anyManaged ? (
          <EntitySelectorFooter>
            <EntitySelectorFooterAction icon="x" onClick={() => apply(null)}>
              No manager
            </EntitySelectorFooterAction>
          </EntitySelectorFooter>
        ) : null}
      </EntitySelectorContent>
    </UserSelector>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export type TeamMembersRosterParts = {
  /** The collapsed roster search (a `/`-target `ViewSearch`), for the host's
   *  action band ahead of the add action. */
  search: React.ReactNode;
  /** The Group chip (off by default; Team Role or Presence), then past a
   *  hairline the quick-filter chips (Status, Role, Reports to — no Team:
   *  this roster IS one team) and the Sort chip — for the host's toolbar
   *  band, leading. */
  quickFilters: React.ReactNode;
  /** The add-members selector; null when the viewer can't manage membership. */
  addAction: React.ReactNode;
  /** The roster table (with its own load skeleton and empty state). */
  table: React.ReactNode;
  teamName: string;
  loading: boolean;
  error: ReturnType<typeof useTeamMembers>["error"];
  retry: () => void;
};

type TeamMembersRosterProps = {
  teamId: string;
  canManage: boolean;
  onViewProfile: (userId: string) => void;
  /** Scrollable top gutter under the host's toolbar band — pass `"md"` from a
   *  mount with a `DataTableToolbar` above the table; the settings page's
   *  `PageHeader` layout omits it. */
  gutterTop?: SpacingToken;
  children: (parts: TeamMembersRosterParts) => React.ReactNode;
};

export function TeamMembersRoster({
  teamId,
  canManage,
  onViewProfile,
  gutterTop,
  children,
}: TeamMembersRosterProps) {
  const { currentUser } = useAppData();
  const { removeTeamMembers, setTeamMemberRole } = useTeamMutations();

  // Rests on the server's own order (`buildOrderBy` falls back to name), so
  // the Sort chip reads "Default" until someone sorts — a resting A–Z pick
  // would light the chip on every visit.
  const [sorting, setSorting] = useState<SortingState>([]);

  // The same name/email/title columns every member list searches (MEMBER_LIST),
  // AND-folded onto the hook's team-membership predicate server-side, so a
  // match beyond the 50-row page still surfaces.
  const [searchValue, setSearchValue] = useState("");
  useTrackSearch(searchValue, () => ({
    context: AnalyticsContext.Member,
    event: MemberEvent.Search,
    data: { via: "team_roster" },
  }));
  const searchOptions = useMemo(
    () => ({ searchWhere: buildSearchWhere(searchValue, MEMBER_LIST.searchFields) }),
    [searchValue],
  );

  // The quick chips' state. Roles ride the members-list document's sibling
  // connection (the same side-load the members table takes when grouped).
  const siblingData = useMembersSiblingData(false);
  const roles = useMemo(
    () => connectionRows(siblingData?.roles),
    [siblingData],
  );
  const quickFilterDefs = useMemo(
    () => buildMemberQuickFilterDefs(undefined, roles),
    [roles],
  );
  const tableFilters = useTableFilters<EntityItemConfig>(quickFilterDefs);
  const queryOptions = useMemo(
    () => ({ ...searchOptions, filterWhere: tableFilters.where }),
    [searchOptions, tableFilters.where],
  );

  const {
    teamName,
    teamMembers,
    existingUserIds,
    loading,
    error,
    refetch,
  } = useTeamMembers(teamId, sorting, queryOptions);

  const [removeTarget, setRemoveTarget] = useState<TeamMember | null>(null);

  const handleConfirmRemove = useCallback(async () => {
    if (!removeTarget) return;
    const removed = await removeTeamMembers(teamId, [removeTarget.userId]);
    if (removed && removeTarget.userId === currentUser?.userId) {
      trackEvent({
        context: AnalyticsContext.Team,
        event: TeamEvent.Leave,
        data: { via: "team_roster" },
      });
    }
    setRemoveTarget(null);
    refetch();
  }, [removeTarget, teamId, removeTeamMembers, refetch, currentUser?.userId]);

  const handleChangeRole = useCallback(
    async (member: TeamMember, newRole: TeamMembershipRole) => {
      if (member.role === newRole) return;
      await setTeamMemberRole(teamId, member.userId, newRole);
      refetch();
    },
    [teamId, setTeamMemberRole, refetch],
  );

  // Reports-to — the same write every other mount of the column commits
  // (null clears; a pick from the member's own line lifts them up). Requires
  // manage.users, so the cell is editable exactly for that permission.
  const canManageUsers = usePermission("manage.users");
  const setReportsTo = useSetReportsTo("team_roster");
  const handleManagerChange = useCallback(
    async (userId: string, managerId: string | null) => {
      await setReportsTo(userId, managerId);
      refetch();
    },
    [setReportsTo, refetch],
  );

  const columns = useTeamMemberColumns({
    canManage,
    onChangeRole: handleChangeRole,
    onManagerChange: canManageUsers ? handleManagerChange : undefined,
  });

  // Bulk selection — the row's edits over many rows: the team role (a
  // Manager/Member pick) and removal (one confirm) for `canManage` viewers,
  // the same gate as the row menu; Reports to for manage.users holders, the
  // same gate as the cell. No gate held = no `bulkActions` = no selection
  // lane, so a read-only roster never offers a selection it can do nothing
  // with. Each action runs the mutations `silent` and reports once through
  // the bar, instead of one toast per member.
  const viewerId = currentUser?.userId;
  const bulkActions = useMemo<BulkAction<TeamMember>[]>(() => {
    const actions: BulkAction<TeamMember>[] = [];
    if (canManage) {
      actions.push({
        id: "change-role",
        type: "option-picker",
        label: "Role",
        dialogLabel: "Change Role...",
        icon: "shield",
        options: TEAM_ROLE_GROUPS.map((g) => ({ id: g.key, label: g.label })),
        // Counts only the members whose role changes, as the write does.
        successMessage: (rows, _count, roleId) => {
          const n = rows.filter((m) => m.role !== roleId).length;
          return `Role updated for ${n} member${n !== 1 ? "s" : ""}`;
        },
        onAction: async (selected, roleId) => {
          const role = roleId as TeamMembershipRole;
          try {
            // Members already in the picked role are left alone: the
            // in-place UPDATE would be a no-op write and a no-op activity.
            await settleAll(
              selected.filter((m) => m.role !== role),
              (m) => setTeamMemberRole(teamId, m.userId, role, { silent: true }),
            );
          } finally {
            refetch();
          }
        },
      });
    }
    if (canManageUsers) {
      actions.push({
        id: "reports-to",
        type: "popover",
        label: "Reports To",
        dialogLabel: "Set Reports To...",
        icon: "user-lock",
        contentProps: { padding: "none" },
        content: ({ selectedRows, commit }) => (
          <BulkReportsToContent
            selectedRows={selectedRows}
            commit={(fn, message) =>
              commit(async (rows) => {
                try {
                  await fn(rows);
                } finally {
                  refetch();
                }
              }, message)
            }
          />
        ),
      });
    }
    if (canManage) {
      actions.push({
        id: "remove",
        type: "confirm",
        label: "Remove",
        dialogLabel: "Remove from Team...",
        // The registry has no user-minus glyph; x-circle is the app's remove
        // verb (Remove photo, Suspend, Cancel invite).
        icon: "x-circle",
        destructive: true,
        confirmTitle: (rows) => {
          if (rows.length === 1) {
            return rows[0]?.userId === viewerId
              ? "Leave team?"
              : `Remove ${rows[0]?.name ?? "teammate"}?`;
          }
          return `Remove ${rows.length} members?`;
        },
        confirmDescription: (rows) => {
          const includesViewer = rows.some((m) => m.userId === viewerId);
          if (includesViewer) {
            const others = rows.length - 1;
            return others === 0
              ? `You will be removed from ${teamName}. You can be re-added later.`
              : `You and ${others} other member${others !== 1 ? "s" : ""} will be removed from ${teamName}. Everyone can be re-added later.`;
          }
          return rows.length === 1
            ? `${rows[0]?.name ?? "This teammate"} will be removed from ${teamName}. They can be re-added later.`
            : `${rows.length} members will be removed from ${teamName}. They can be re-added later.`;
        },
        confirmLabel: "Remove from Team",
        successMessage: (_rows, count) =>
          `${count} member${count !== 1 ? "s" : ""} removed from ${teamName}`,
        onAction: async (selected) => {
          const userIds = selected.map((m) => m.userId);
          try {
            await removeTeamMembers(teamId, userIds, { silent: true });
          } finally {
            refetch();
          }
          if (viewerId && userIds.includes(viewerId)) {
            trackEvent({
              context: AnalyticsContext.Team,
              event: TeamEvent.Leave,
              data: { via: "team_roster_bulk" },
            });
          }
        },
      });
    }
    return actions;
  }, [
    canManage,
    canManageUsers,
    teamId,
    teamName,
    viewerId,
    setTeamMemberRole,
    removeTeamMembers,
    refetch,
  ]);
  const tableView = useTableViewSettings("team-members", {
    defaultHiddenColumnIds: ROSTER_DEFAULT_HIDDEN_COLUMN_IDS,
  });

  // Grouping — off by default (a roster rests as one flat list). Both fields
  // partition the fetched rows client-side: role is derived on the row and
  // presence is the live store, so a query per bucket would buy nothing.
  const {
    groupByField,
    setGroupByField,
    isGrouped,
    groupByLabels,
    groupByIcons,
    activeGroupConfig,
    groupSortDirection,
    setGroupSortDirection,
  } = useDataTableState({
    columns,
    groupFields: ROSTER_GROUP_FIELDS,
  });

  const renderRowActions = useCallback(
    (member: TeamMember) => {
      const isManager = member.role === "lead";
      const isCurrentUser = member.userId === currentUser?.userId;

      return (
        <>
          <MenuItem
            onClick={() => handleChangeRole(member, isManager ? "member" : "lead")}
          >
            <Icon name="shield" color="muted" />
            {isManager ? "Demote to Member..." : "Promote to Manager..."}
          </MenuItem>
          <MenuItem onClick={() => onViewProfile(member.userId)}>
            <Icon name="user" color="muted" />
            View profile
          </MenuItem>
          <MenuSeparator />
          <MenuItem onClick={() => setRemoveTarget(member)}>
            <Icon name={isCurrentUser ? "log-out" : "user"} color="muted" />
            {isCurrentUser ? "Leave Team..." : "Remove from Team..."}
          </MenuItem>
        </>
      );
    },
    [handleChangeRole, onViewProfile, currentUser?.userId],
  );

  const { states, registerState, getGroupProps } = useGroupStates<TeamMember>();

  const visibleGroups = useMemo(
    () =>
      isGrouped && activeGroupConfig
        ? resolveVisibleGroups(
            activeGroupConfig,
            tableFilters.filters,
            groupSortDirection,
          )
        : [],
    [isGrouped, activeGroupConfig, tableFilters.filters, groupSortDirection],
  );

  // Fixed-enum buckets hide when empty (the members directory's status-bucket
  // rule): an inert "Away 0" header is noise on a roster where nobody is
  // away. A bucket stays visible until its total is known, and when every
  // bucket is empty all of them stay so the view doesn't render blank.
  const groups = useMemo<DataTableGroupType<TeamMember>[]>(() => {
    if (!isGrouped) {
      return [
        {
          key: "__all__",
          label: "All",
          totalCount: states.get("__all__")?.total,
          ...getGroupProps("__all__"),
        },
      ];
    }
    const populated = visibleGroups.filter((g) => {
      const state = states.get(g.key);
      return !state || state.loading || state.total > 0;
    });
    const shown = populated.length > 0 ? populated : visibleGroups;
    return shown.map((g) => ({
      key: g.key,
      label: g.label,
      totalCount: states.get(g.key)?.total,
      ...getGroupProps(g.key),
    }));
  }, [isGrouped, visibleGroups, states, getGroupProps]);

  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const search = (
    <ViewSearch
      onValueChange={setSearchValue}
      size="xs"
    />
  );

  // The counts beside the chips' options run over this team's roster as the
  // search narrows it — the same base the members query itself starts from.
  const countScope = useMemo(
    () =>
      combineWhereConditions(
        [{ teamIds: { hasSome: [teamId] } }, searchOptions.searchWhere].filter(
          (w): w is Record<string, unknown> => w != null,
        ),
      ),
    [teamId, searchOptions.searchWhere],
  );

  const quickFilters = (
    <>
      <DataTableGroupByDropdown
        groupByField={groupByField}
        setGroupByField={setGroupByField}
        groupByLabels={groupByLabels}
        groupByIcons={groupByIcons}
        groupSortDirection={groupSortDirection}
        setGroupSortDirection={setGroupSortDirection}
        // The members directory's chip treatment — except the resting state
        // here is UNgrouped, so any grouping lights the chip and its ×
        // clears back to the flat list.
        trigger={
          <MemberGroupChip
            value={isGrouped ? groupByLabels[groupByField] : "None"}
            selected={isGrouped}
            onClear={() => setGroupByField("none")}
            clearLabel="Clear grouping"
          />
        }
      />
      <MemberQuickFiltersDivider />
      <MemberQuickFilters
        filters={tableFilters}
        roles={roles}
        scopeWhere={countScope}
        via="team_roster"
      />
      <MemberQuickFiltersDivider />
      <MemberSortChip sorting={sorting} onSortingChange={setSorting} via="team_roster" />
    </>
  );
  const narrowed = !!searchValue || tableFilters.activeFilterCount > 0;

  // The picker excludes the team detail's own rosters (leads + members),
  // not the search-filtered connection, so a member hidden by the search
  // can still never be re-added.
  const addAction = canManage ? (
    <TeamAddMemberAction teamId={teamId} existingUserIds={existingUserIds} />
  ) : null;

  const providersContextKey = `${teamId}|${JSON.stringify(sorting)}|${JSON.stringify(tableFilters.where ?? null)}`;

  const table = (
    // A bounded flex column: the table scrolls internally — its `gutterTop`,
    // row virtualization and the bulk bar's seat at the bottom edge all
    // assume the table root IS the scroll box — so a host mounts `table` in
    // a flex column, never inside a page scroller.
    <Box direction="column" fill="flex" minHeight={0}>
      {!isGrouped ? (
        <LocalGroupProvider<TeamMember>
          groupKey="__all__"
          rows={teamMembers}
          contextKey={providersContextKey}
          onStateChange={registerState}
        />
      ) : groupByField === "role" ? (
        <RoleGroupProviders
          visibleGroups={visibleGroups}
          rows={teamMembers}
          contextKey={providersContextKey}
          onStateChange={registerState}
        />
      ) : (
        <PresenceGroupProviders
          visibleGroups={visibleGroups}
          rows={teamMembers}
          contextKey={providersContextKey}
          onStateChange={registerState}
        />
      )}
      <DataTable
        columns={columns}
        groups={groups}
        getRowId={(row) => row.userId}
        gutterTop={gutterTop}
        sorting={sorting}
        onSortingChange={setSorting}
        density="regular"
        pinnedColumnIds={ROSTER_PINNED_COLUMN_IDS}
        paddingX="md"
        {...tableView.tableProps}
        onColumnVisibilityChange={tableView.settingsProps.onColumnVisibilityChange}
        renderRowActions={canManage ? renderRowActions : undefined}
        bulkActions={bulkActions.length > 0 ? bulkActions : undefined}
        bulkSelectionNoun={MEMBER_SELECTION_NOUN}
        emptyState={
          <DataTableEmpty
            message={
              narrowed
                ? "No members match your filters"
                : "No teammates on this team yet"
            }
          />
        }
      />
    </Box>
  );

  return (
    <>
      {children({
        search,
        quickFilters,
        addAction,
        table,
        teamName,
        loading,
        error,
        retry,
      })}

      {canManage ? (
        <AlertDialog
          open={!!removeTarget}
          onOpenChange={(open) => { if (!open) setRemoveTarget(null); }}
        >
          <AlertDialogPopup>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {removeTarget?.userId === currentUser?.userId
                  ? "Leave team?"
                  : `Remove ${removeTarget?.name ?? "teammate"}?`}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {removeTarget?.userId === currentUser?.userId
                  ? `You will be removed from ${teamName}. You can be re-added later.`
                  : `${removeTarget?.name ?? "This teammate"} will be removed from ${teamName}. They can be re-added later.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogClose render={<Button variant="outline" />}>
                Cancel
              </AlertDialogClose>
              <Button color="destructive" onClick={handleConfirmRemove}>
                {removeTarget?.userId === currentUser?.userId ? "Leave Team" : "Remove from Team"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogPopup>
        </AlertDialog>
      ) : null}
    </>
  );
}
